import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import styled, { css } from 'styled-components'

import {
  ChevronLeftExactIcon,
  ChevronRightExactIcon,
} from '@root/shared/ui-kit'

type Props = {
  children: React.ReactNode
  scrollRef: React.RefObject<HTMLElement | null>
  transformScrollBehavior?: boolean
  arrowsBackground?: string
}
export function useHorizontalScroll(args: {
  disabled?: boolean
  scrollTarget: React.RefObject<HTMLElement | null>
  scrollCapture: React.RefObject<HTMLElement | null>
}) {
  useEffect(() => {
    if (args.disabled) {
      return
    }
    const targetElement = args.scrollTarget.current
    const captureElement = args.scrollCapture.current

    if (!targetElement || !captureElement) {
      return
    }

    const onWheel = (evt: WheelEvent) => {
      evt.preventDefault()

      targetElement.scrollLeft += evt.deltaY + evt.deltaX
    }

    captureElement.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      captureElement.removeEventListener('wheel', onWheel)
    }
  }, [args.disabled, args.scrollCapture, args.scrollTarget])
}

const SCROLL_EDGE_TOLERANCE_PX = 1

function useHorizontalScrollEdges(args: {
  scrollTarget: React.RefObject<HTMLElement | null>
  scrollCapture: React.RefObject<HTMLElement | null>
}) {
  const [edges, setEdges] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  })

  useLayoutEffect(() => {
    const target = args.scrollTarget.current
    if (!target) {
      return
    }

    const update = () => {
      const maxScrollLeft = target.scrollWidth - target.clientWidth
      const canScrollLeft = target.scrollLeft > SCROLL_EDGE_TOLERANCE_PX
      const canScrollRight =
        target.scrollLeft < maxScrollLeft - SCROLL_EDGE_TOLERANCE_PX

      setEdges((prev) =>
        prev.canScrollLeft === canScrollLeft &&
        prev.canScrollRight === canScrollRight
          ? prev
          : { canScrollLeft, canScrollRight },
      )
    }

    update()

    target.addEventListener('scroll', update, { passive: true })

    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(target)

    const capture = args.scrollCapture.current
    if (capture && capture !== target) {
      resizeObserver.observe(capture)
    }

    const mutationObserver = new MutationObserver(update)
    mutationObserver.observe(target, { childList: true, subtree: true })

    return () => {
      target.removeEventListener('scroll', update)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [args.scrollCapture, args.scrollTarget])

  return edges
}

export const HorizontalList = ({
  scrollRef,
  children,
  transformScrollBehavior = false,
  arrowsBackground,
}: Props) => {
  const scrollableContainerRef = useRef<HTMLDivElement>(null)
  const scroll = useCallback(
    (direction: 'left' | 'right') => {
      const el = scrollRef.current
      if (!el) {
        return
      }
      const itemWidth = el.firstElementChild?.clientWidth || 150
      const sign = direction === 'left' ? -1 : 1
      el.scrollBy({
        left: itemWidth * sign,
        behavior: 'smooth',
      })
    },
    [scrollRef],
  )
  useHorizontalScroll({
    disabled: !transformScrollBehavior,
    scrollCapture: scrollableContainerRef,
    scrollTarget: scrollRef,
  })
  const { canScrollLeft, canScrollRight } = useHorizontalScrollEdges({
    scrollCapture: scrollableContainerRef,
    scrollTarget: scrollRef,
  })

  return (
    <Root>
      <ScrollableContainer ref={scrollableContainerRef}>
        {children}
      </ScrollableContainer>
      {canScrollLeft ? (
        <NavigationButton
          className="topbar-favs-navigation"
          $side="left"
          type="button"
          onClick={() => scroll('left')}
          $background={arrowsBackground}
        >
          <ChevronLeftExactIcon size={10} />
        </NavigationButton>
      ) : undefined}
      {canScrollRight ? (
        <NavigationButton
          className="topbar-favs-navigation"
          $side="right"
          type="button"
          onClick={() => scroll('right')}
          $background={arrowsBackground}
        >
          <ChevronRightExactIcon size={10} />
        </NavigationButton>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div`
  position: relative;
  overflow: hidden;

  &:hover > .topbar-favs-navigation {
    opacity: 0.6;
  }
`
const NavigationButton = styled.button<{
  $side: 'left' | 'right'
  $background?: string
}>`
  all: unset;
  position: absolute;
  ${(x) =>
    x.$side === 'left'
      ? css`
          left: 0;
        `
      : css`
          right: 0;
        `}
  top: 0;
  bottom: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  background: ${(x) => x.$background ?? 'var(--appearance-1)'};
  padding: 2px 4px;
  opacity: 0;
  transition: all 0.2s ease;

  &:hover,
  &:focus-visible {
    opacity: 1 !important;
  }
`

const ScrollableContainer = styled.div``
